/**
 * job_assets_controller.js — the assets section on a job page (Stimulus).
 *
 * The forms post normally and the server answers with a Turbo Stream that
 * replaces the whole section, so there is deliberately very little here: no
 * fetch, no optimistic rendering, no client-side state to drift out of sync
 * with what was actually saved. A reading that appears in the UI but never
 * reached the database is the worst outcome this feature has.
 *
 * What it owns: dismissing the modal after a successful submit, so the
 * technician lands on the refreshed list instead of on a stale form over the
 * top of it. Bootstrap owns the modals themselves (static markup +
 * data-bs-toggle) — see the note in asset_picker_modal.html about why these are
 * not lazy turbo-frames.
 *
 * The listener sits on `document`, not `this.element`, because the modals are
 * rendered after this controller's element, outside .section-card (see
 * job_assets.html) — an element-scoped listener would never see their submits. It is filtered to this
 * job's own asset endpoints so it cannot react to anything else on the page,
 * and it is removed in disconnect() so a Turbo navigation leaves nothing bound.
 */
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static values = { jobId: Number };

    connect() {
        this._prefix = `/job/${this.jobIdValue}/assets`;
        this._onSubmitEnd = (event) => this._maybeCloseModal(event);
        document.addEventListener('turbo:submit-end', this._onSubmitEnd);
    }

    disconnect() {
        document.removeEventListener('turbo:submit-end', this._onSubmitEnd);
        this._onSubmitEnd = null;
    }

    _maybeCloseModal(event) {
        const form = event.target;
        if (!form || !form.action || !form.action.includes(this._prefix)) return;
        if (event.detail && event.detail.success === false) return;

        const modal = form.closest('.modal');
        if (!modal || !window.bootstrap) return;

        const instance = window.bootstrap.Modal.getInstance(modal);
        if (instance) instance.hide();
    }
}
